/**
 * PATH: packages/email-hub/src/feature/stores/mutedCategoriesStore.ts
 * FILE_ID: EMAILHUB-FEATURE-STORE-MUTEDCATS-001
 * PURPOSE: Per-user set of email categories hidden from the default inbox
 *          view ("this kind of mail should go somewhere, not my inbox").
 * WHY: Operator 2026-07-03, looking at a Facebook notification: "an email
 *      like this I don't really always want to see... so it should go
 *      somewhere". Muting the category routes that class of mail out of
 *      the default list; the category chip still reveals it on demand.
 * FLOW:
 *   +--------------+   +---------------------+   +----------------------+
 *   | localStorage |-->| module state +      |-->| InboxPage adds       |
 *   | (instant)    |   | pub/sub subscribers |   | category__not=<muted> |
 *   +--------------+   +---------------------+   +----------------------+
 *          ^ hydrate once from the durable per-user KV, then debounce-save
 * RELATED:
 *   - pages/InboxPage.tsx: merges muted keys into effectiveExcludeCategories
 *   - components/CategoryChip.tsx: the "Hide from inbox" action
 *   - @quotech/tenant-shared/preferences/dbViewPreference: durable KV tier
 */

import { useEffect, useState } from 'react'
import {
  fetchServerState,
  scheduleServerSave,
} from '@quotech/tenant-shared/preferences/dbViewPreference'

const LOCAL_KEY = 'email_inbox_muted_categories'
const DB_SCOPE = 'email:muted-categories'

let muted: string[] = readLocal()
let hydrated = false
let touched = false
const subscribers = new Set<(next: string[]) => void>()

function readLocal(): string[] {
  try {
    const raw = localStorage.getItem(LOCAL_KEY)
    const arr = raw ? JSON.parse(raw) : []
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : []
  } catch {
    return []
  }
}

function persist(next: string[]): void {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(next))
  } catch {
    // ignore storage failures
  }
  scheduleServerSave(DB_SCOPE, { muted: next })
}

function set(next: string[], fromServer = false): void {
  muted = next
  if (!fromServer) {
    touched = true
    persist(next)
  }
  subscribers.forEach((fn) => fn(next))
}

async function hydrateOnce(): Promise<void> {
  if (hydrated) return
  hydrated = true
  try {
    const s = await fetchServerState<{ muted: string[] }>(DB_SCOPE, {
      muted: [],
    })
    // Server wins unless the operator already flipped something this session.
    if (!touched && s && Array.isArray(s.muted)) {
      const clean = s.muted.filter((x) => typeof x === 'string')
      muted = clean
      try {
        localStorage.setItem(LOCAL_KEY, JSON.stringify(clean))
      } catch {
        // ignore storage failures
      }
      subscribers.forEach((fn) => fn(clean))
    }
  } catch {
    // Fail-soft: localStorage tier already painted.
  }
}

/** Hide a category's rows from the default inbox view. */
export function muteCategory(key: string): void {
  if (!key || muted.includes(key)) return
  set([...muted, key])
}

/** Bring a category's rows back into the default inbox view. */
export function unmuteCategory(key: string): void {
  if (!muted.includes(key)) return
  set(muted.filter((k) => k !== key))
}

export function isCategoryMuted(key: string): boolean {
  return muted.includes(key)
}

/**
 * The viewer's muted category keys (shared module state; every consumer
 * re-renders on change). Hydrates once from the durable per-user store.
 */
export function useMutedCategories(): string[] {
  const [state, setState] = useState<string[]>(muted)
  useEffect(() => {
    let alive = true
    const sub = (next: string[]) => {
      if (alive) setState(next)
    }
    subscribers.add(sub)
    void hydrateOnce()
    return () => {
      alive = false
      subscribers.delete(sub)
    }
  }, [])
  return state
}

/** Test-only: reset module state so each test starts clean. */
export function __resetMutedCategoriesStore(): void {
  muted = []
  hydrated = false
  touched = false
  subscribers.clear()
}
